import { useCallback, useEffect, useRef, useState } from "react";
import { cachedCover, resolveCover, bustCover } from "../lib/events";

// Lazily resolve a display-sized signed cover URL for a single card, using the
// shared cache in events.js. Signing happens only for cards that actually mount,
// and repeated appearances of the same image reuse the cached URL — so image work
// is bounded by what's rendered, not by the total number of events in the DB.
// `onError` re-signs the URL once if the browser reports the image failed (e.g.
// the temporary signed URL expired while the app was left open), so images
// recover automatically without a full reload.
export function useCoverUrl(coverPath, hero = false) {
  const [url, setUrl] = useState(() => (coverPath ? cachedCover(coverPath, hero) : null));
  const retriedRef = useRef(false);

  useEffect(() => {
    retriedRef.current = false;
    if (!coverPath) {
      setUrl(null);
      return;
    }
    const hit = cachedCover(coverPath, hero);
    if (hit) {
      setUrl(hit);
      return;
    }
    let alive = true;
    resolveCover(coverPath, hero).then((u) => {
      if (alive) setUrl(u || null);
    });
    return () => {
      alive = false;
    };
  }, [coverPath, hero]);

  const onError = useCallback(() => {
    if (!coverPath || retriedRef.current) return; // signed cover only, once
    retriedRef.current = true;
    bustCover(coverPath, hero);
    resolveCover(coverPath, hero).then((u) => {
      if (u) setUrl(u);
    });
  }, [coverPath, hero]);

  return { url, onError };
}
